import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { validateUsername } from '@/lib/utils';
import { ZapOverlay } from './ZapOverlay';
import { FairyTaleOverlay } from './FairyTaleOverlay';
import { TUIRegistration } from './TUIRegistration';
import type { Profile } from '@/types';

interface Props {
  onClose: () => void;
}

type Phase = 'intro' | 'idle' | 'logging-in' | 'registering' | 'accepting' | 'pill-choice' | 'zapping' | 'fairy-tale';
type LoginStep = 'username' | 'password' | 'submitting';

const RULES_LINES = [
  '',
  'RULES // ERH COMMUNITY STANDARDS',
  '---------------------------------',
  '1) No doxxing. No private info.',
  '2) No threats. No violence. No organizing harm.',
  '3) No hate/bigotry toward protected groups.',
  '4) No targeted harassment. Argue claims with evidence.',
  '5) Mark speculation. Link sources when possible.',
  '6) No illegal content or instructions.',
  '7) Mods enforce these rules. Violations = removal/ban.',
  '',
  'Type /accept to agree and choose your path.',
];

const PILL_CHOICE_LINES = [
  '',
  'BLUE PILL — return to comfort. Type /blue',
  'RED PILL  — go deeper. Type /red',
];

// ─── INTRO SEQUENCE ──────────────────────────────────────────────────────────

const INTRO_TEXT =
`Welcome Friend.

It appears you've followed the White Rabbit...

You were not brought here by accident.

Something in you has always known
that this world is not what it pretends to be.

A quiet signal beneath the noise.
A pattern beneath the chaos.
A presence just beyond explanation,
calling some to sleep… and others to seek.

Most ignore it.

You didn't.

So now you stand at the threshold.

Take the blue pill, and return to the comfort of distraction —
the routine, the script, the illusion.

Take the red pill, and step beyond the veil.
Follow the signal. Question everything.
See how deep the Electric Rabbit Hole really goes.

But understand this:
once you choose to see,
you cannot go back to blindness.

The choice is yours.`;

const ASCII_ART =
` _____ _           _        _
| ____| | ___  ___| |_ _ __(_) ___
|  _| | |/ _ \\/ __| __| '__| |/ __|
| |___| |  __/ (__| |_| |  | | (__
|_____|_|\\___|\\___|\\__|_|  |_|\\___|
 ____       _     _     _ _
|  _ \\ __ _| |__ | |__ (_) |_
| |_) / _\` | '_ \\| '_ \\| | __|
|  _ < (_| | |_) | |_) | | |_
|_| \\_\\__,_|_.__/|_.__/|_|\\__|
 _   _       _
| | | | ___ | | ___
| |_| |/ _ \\| |/ _ \\
|  _  | (_) | |  __/
|_| |_|\\___/|_|\\___|`;

// ─── INITIAL STATE ────────────────────────────────────────────────────────────

const getInitialState = (): { phase: Phase; lines: string[] } => {
  const { session, profile } = useAuthStore.getState();
  if (!session) return { phase: 'intro', lines: [] };
  if (!profile || profile.pill_choice) return { phase: 'idle', lines: [] };
  if (profile.terms_accepted_at) {
    return { phase: 'pill-choice', lines: [...RULES_LINES, ...PILL_CHOICE_LINES] };
  }
  return { phase: 'accepting', lines: RULES_LINES };
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export function LoginTerminal({ onClose }: Props) {
  const { login, updateProfile, fetchProfile } = useAuthStore();
  const { enterRabbitHole } = useThemeStore();

  const initial = getInitialState();
  const [phase, setPhase] = useState<Phase>(initial.phase);
  const [lines, setLines] = useState<string[]>(initial.lines);
  const [loginStep, setLoginStep] = useState<LoginStep>('username');
  const [loginUsername, setLoginUsername] = useState('');
  const [input, setInput] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [saving, setSaving] = useState(false);

  // Intro-specific state
  const [blinkTick, setBlinkTick] = useState(0);
  const [introStarted, setIntroStarted] = useState(false);
  const [typedCount, setTypedCount] = useState(0);
  const introTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  const push = (text: string) => {
    setLines(prev => [...prev, text]);
    setTimeout(() => {
      if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }, 0);
  };

  // Refocus input on phase/step change (non-overlay/intro phases)
  useEffect(() => {
    if (!['registering', 'zapping', 'fairy-tale', 'intro'].includes(phase)) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [phase, loginStep]);

  // ─── INTRO: cursor blinks 3×, then typing begins ─────────────────────────
  useEffect(() => {
    if (phase !== 'intro') return;
    let tick = 0;
    const blink = () => {
      tick++;
      setBlinkTick(tick);
      if (tick < 6) {
        introTimerRef.current = setTimeout(blink, 300);
      } else {
        introTimerRef.current = setTimeout(() => setIntroStarted(true), 400);
      }
    };
    introTimerRef.current = setTimeout(blink, 400);
    return () => { if (introTimerRef.current) clearTimeout(introTimerRef.current); };
  }, [phase]);

  // ─── INTRO: typewriter at 1.5× speed with natural cadence ────────────────
  useEffect(() => {
    if (phase !== 'intro' || !introStarted || typedCount >= INTRO_TEXT.length) return;
    const ch = INTRO_TEXT[typedCount];
    const next = INTRO_TEXT[typedCount + 1];
    const prev = typedCount > 0 ? INTRO_TEXT[typedCount - 1] : '';
    let delay = 26;
    if (ch === '.') {
      // Triple dot (ellipsis) — long dramatic pause on the last dot
      delay += prev === '.' ? 320 : 260;
    } else if (ch === ',') {
      delay += 110;
    } else if (ch === '—') {
      delay += 220;
    } else if (ch === '…') {
      delay += 340;
    } else if (ch === '!') {
      delay += 200;
    } else if (ch === '\n') {
      delay += next === '\n' ? 420 : 70;
    }
    introTimerRef.current = setTimeout(() => setTypedCount(c => c + 1), delay);
    return () => { if (introTimerRef.current) clearTimeout(introTimerRef.current); };
  }, [typedCount, introStarted, phase]);

  // ─── INTRO: keep output scrolled to bottom while typing ──────────────────
  useEffect(() => {
    if (phase !== 'intro' || !outputRef.current) return;
    outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [typedCount, phase]);

  // ─── INTRO: auto-focus input when typing finishes ────────────────────────
  useEffect(() => {
    if (phase === 'intro' && typedCount >= INTRO_TEXT.length) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [typedCount, phase]);

  // ─── INTRO: space / enter skips animation ────────────────────────────────
  const skipIntro = useCallback(() => {
    if (introTimerRef.current) clearTimeout(introTimerRef.current);
    setIntroStarted(true);
    setTypedCount(INTRO_TEXT.length);
  }, []);

  useEffect(() => {
    if (phase !== 'intro') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        // Don't intercept if user is typing in the command input
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        skipIntro();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, skipIntro]);

  // ─────────────────────────────────────────────────────────────────────────

  const afterAuth = (profile: Profile | null) => {
    if (profile?.pill_choice === 'red') {
      enterRabbitHole();
      onClose();
    } else if (profile?.pill_choice === 'blue') {
      onClose();
    } else if (profile?.terms_accepted_at) {
      PILL_CHOICE_LINES.forEach(push);
      setPhase('pill-choice');
    } else {
      RULES_LINES.forEach(push);
      setPhase('accepting');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = input.trim();

    // ── Intro phase ────────────────────────────────────────────────────────
    if (phase === 'intro') {
      if (typedCount < INTRO_TEXT.length) {
        skipIntro();
        return;
      }
      if (!val) return;
      setInput('');
      setFieldError('');
      const cmd = val.toLowerCase();
      if (cmd === '/login') {
        push('Initiating login sequence...');
        setLoginStep('username');
        setLoginUsername('');
        setPhase('logging-in');
      } else if (cmd === '/create-account') {
        setPhase('registering');
      } else if (cmd === '/help') {
        push('Commands: /login  /create-account');
        setPhase('idle');
      } else {
        push(`Unknown command: ${val}. Type /login or /create-account.`);
        setPhase('idle');
      }
      return;
    }

    if (!val || saving || (phase === 'logging-in' && loginStep === 'submitting')) return;
    setInput('');
    setFieldError('');

    // ── Idle phase ─────────────────────────────────────────────────────────
    if (phase === 'idle') {
      push(`$ ${val}`);
      const cmd = val.toLowerCase();
      if (cmd === '/login') {
        push('Initiating login sequence...');
        setPhase('logging-in');
        setLoginStep('username');
        setLoginUsername('');
      } else if (cmd === '/create-account') {
        setPhase('registering');
      } else if (cmd === '/help') {
        push('Available commands: /login  /create-account  /help');
      } else {
        push(`Unknown command: ${val}. Type /help.`);
      }
      return;
    }

    // ── Login flow ─────────────────────────────────────────────────────────
    if (phase === 'logging-in') {
      if (loginStep === 'username') {
        const err = validateUsername(val);
        if (err) { setFieldError(err); return; }
        push(`IDENTIFICATION: ${val}`);
        setLoginUsername(val);
        setLoginStep('password');
      } else if (loginStep === 'password') {
        push(`ENCRYPTION KEY: ${'•'.repeat(Math.min(val.length, 12))}`);
        setLoginStep('submitting');
        push('AUTHENTICATING...');
        login(loginUsername, val)
          .then(() => {
            push('ACCESS GRANTED.');
            afterAuth(useAuthStore.getState().profile);
          })
          .catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Authentication failed.';
            push(`ERROR: ${msg.toUpperCase()}`);
            setLoginStep('password');
          });
      }
      return;
    }

    // ── Accepting phase ────────────────────────────────────────────────────
    if (phase === 'accepting') {
      push(`$ ${val}`);
      if (val.toLowerCase() === '/accept') {
        setSaving(true);
        push('PROCESSING CLEARANCE...');

        // Ensure profile is loaded (may be null after registration race)
        const ensureProfile = async () => {
          const { profile, session } = useAuthStore.getState();
          if (!profile && session?.user) {
            await fetchProfile(session.user.id);
          }
          return useAuthStore.getState().profile;
        };

        ensureProfile().then((p) => {
          if (!p) throw new Error('COULD NOT LOAD PROFILE. LOG OUT AND BACK IN.');
          return updateProfile({
            terms_accepted_at: new Date().toISOString(),
            terms_version: '1',
          });
        }).then(() => {
          setSaving(false);
          push('CLEARANCE CONFIRMED.');
          push('');
          push('BLUE PILL — return to comfort. Type /blue');
          push('RED PILL  — go deeper. Type /red');
          setPhase('pill-choice');
        }).catch((err: unknown) => {
          setSaving(false);
          const msg = err instanceof Error
            ? err.message
            : (typeof err === 'object' && err !== null && 'message' in err)
              ? String((err as Record<string, unknown>).message)
              : 'FAILED TO SAVE CLEARANCE. TRY AGAIN.';
          push(`ERROR: ${msg.toUpperCase()}`);
        });
      } else {
        push('Type /accept to agree to the rules.');
      }
      return;
    }

    // ── Pill choice ────────────────────────────────────────────────────────
    if (phase === 'pill-choice') {
      push(`$ ${val}`);
      const cmd = val.toLowerCase();

      const ensureProfile = async () => {
        const { profile, session } = useAuthStore.getState();
        if (!profile && session?.user) {
          await fetchProfile(session.user.id);
        }
        if (!useAuthStore.getState().profile) throw new Error('COULD NOT LOAD PROFILE. LOG OUT AND BACK IN.');
      };

      if (cmd === '/red') {
        setSaving(true);
        push('RED PILL SELECTED. INITIALIZING...');
        ensureProfile().then(() => updateProfile({ pill_choice: 'red' })).then(() => {
          setSaving(false);
          setPhase('zapping');
        }).catch(() => {
          setSaving(false);
          push('ERROR: FAILED TO SAVE CHOICE. TRY AGAIN.');
        });
      } else if (cmd === '/blue') {
        setSaving(true);
        push('BLUE PILL SELECTED. RETURNING TO COMFORT...');
        ensureProfile().then(() => updateProfile({ pill_choice: 'blue' })).then(() => {
          setSaving(false);
          setPhase('fairy-tale');
        }).catch(() => {
          setSaving(false);
          push('ERROR: FAILED TO SAVE CHOICE. TRY AGAIN.');
        });
      } else {
        push('Type /red or /blue to choose your path.');
      }
    }
  };

  const handleRegistrationClose = () => {
    const latest = useAuthStore.getState();
    if (latest.session) {
      afterAuth(latest.profile);
    } else {
      setPhase('idle');
    }
  };

  const handleZapComplete = () => { enterRabbitHole(); onClose(); };
  const handleFairyTaleComplete = () => { void useAuthStore.getState().logout(); onClose(); };

  if (phase === 'registering') return <TUIRegistration onClose={handleRegistrationClose} />;
  if (phase === 'zapping') return <ZapOverlay onComplete={handleZapComplete} />;
  if (phase === 'fairy-tale') return <FairyTaleOverlay onComplete={handleFairyTaleComplete} />;

  // ─── INTRO RENDER ─────────────────────────────────────────────────────────
  if (phase === 'intro') {
    const isTypingDone = typedCount >= INTRO_TEXT.length;
    const displayText = INTRO_TEXT.slice(0, typedCount);

    return (
      <div className="fixed inset-0 z-[300000] flex items-center justify-center bg-black/70">
        <div className="relative flex flex-col bg-matrix-black border border-matrix-green/30 shadow-2xl w-[560px] max-h-[90vh] overflow-hidden terminal-manifest">
          <div className="crt-overlay opacity-40" />
          <div className="noise-overlay opacity-10" />
          <div className="relative z-10 flex flex-col" style={{ maxHeight: '90vh' }}>

            {/* Header */}
            <div className="shrink-0 border-b border-matrix-green/20 px-4 py-2 flex items-center justify-between">
              <span className="font-code text-[10px] text-matrix-green/60 uppercase tracking-widest">
                ERH // SECURE TERMINAL
              </span>
              <button
                onClick={onClose}
                className="font-code text-[10px] text-matrix-green/20 hover:text-alert-red transition-colors"
              >
                [ESC]
              </button>
            </div>

            {/* Output */}
            <div
              ref={outputRef}
              className="overflow-y-auto p-6 win98-scrollbar"
              style={{ minHeight: '400px', maxHeight: 'calc(90vh - 88px)' }}
            >
              {/* Blinking cursor before typing starts */}
              {!introStarted && (
                <span
                  className="font-code text-2xl text-matrix-green"
                  style={{ opacity: blinkTick % 2 === 0 ? 1 : 0 }}
                >
                  █
                </span>
              )}

              {/* Typewriter text */}
              {introStarted && (
                <pre className="font-code text-sm text-matrix-green/90 whitespace-pre-wrap leading-loose">
                  {displayText}
                  {!isTypingDone && <span className="term-cursor text-matrix-green">█</span>}
                </pre>
              )}

              {/* ASCII art + login instructions — shown when typing finishes */}
              {isTypingDone && (
                <div className="mt-6">
                  <pre className="font-code text-[10px] text-matrix-green leading-[1.35] select-none">
                    {ASCII_ART}
                  </pre>
                  <div className="mt-4 border-t border-matrix-green/20 pt-3">
                    <p className="font-code text-xs text-matrix-green/50">
                      Type{' '}
                      <span className="text-matrix-cyan">/login</span>
                      {' '}to enter ·{' '}
                      <span className="text-matrix-cyan">/create-account</span>
                      {' '}to register
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Input / skip prompt */}
            <form
              onSubmit={handleSubmit}
              className="shrink-0 border-t border-matrix-green/20 p-3 flex items-center gap-2"
            >
              {!isTypingDone ? (
                <button
                  type="button"
                  onClick={skipIntro}
                  className="w-full text-left font-code text-xs text-matrix-green/25 hover:text-matrix-green/50 transition-colors"
                >
                  [ press space · enter · or click here to skip ]
                </button>
              ) : (
                <>
                  <span className="font-code text-matrix-green text-sm select-none">$</span>
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="/login  /create-account"
                    autoFocus
                    autoComplete="off"
                    className="flex-1 bg-transparent border-b border-matrix-green/50 text-matrix-green font-code text-sm pb-1 focus:outline-none focus:border-matrix-green caret-matrix-green placeholder:text-matrix-green/20"
                  />
                </>
              )}
            </form>

          </div>
        </div>
      </div>
    );
  }

  // ─── NORMAL TERMINAL RENDER (idle / logging-in / accepting / pill-choice) ─
  const isPasswordField = phase === 'logging-in' && loginStep === 'password';
  const inputDisabled = (phase === 'logging-in' && loginStep === 'submitting') || saving;

  const placeholder =
    phase === 'idle'       ? '/login  /create-account  /help' :
    loginStep === 'username' ? 'codename' :
    loginStep === 'password' ? '••••••••' :
    phase === 'accepting'  ? '/accept' :
    phase === 'pill-choice' ? '/red  or  /blue' : '';

  const promptLabel =
    phase === 'logging-in' && loginStep === 'username' ? '> IDENTIFICATION:' :
    phase === 'logging-in' && loginStep === 'password'  ? '> ENCRYPTION KEY:' :
    null;

  return (
    <div className="fixed inset-0 z-[300000] flex items-center justify-center bg-black/60">
      <div className="relative flex flex-col bg-matrix-black border border-matrix-green/30 shadow-2xl w-[500px] overflow-hidden">
        <div className="crt-overlay opacity-40" />
        <div className="noise-overlay opacity-10" />

        <div className="relative z-10 flex flex-col">
          {/* Header */}
          <div className="shrink-0 border-b border-matrix-green/20 px-4 py-2 flex items-center justify-between">
            <span className="font-code text-[10px] text-matrix-green/60 uppercase tracking-widest">
              ERH // SECURE TERMINAL
            </span>
            <button
              onClick={onClose}
              className="font-code text-[10px] text-matrix-green/20 hover:text-alert-red transition-colors"
            >
              [ESC]
            </button>
          </div>

          {/* Output */}
          <div ref={outputRef} className="overflow-y-auto p-4 space-y-1 win98-scrollbar h-[300px]">
            {lines.length === 0 && (
              <div className="space-y-1">
                <pre className="font-code text-[10px] text-matrix-green leading-tight select-none">{`
╔══════════════════════════════════╗
║   ELECTRIC RABBIT HOLE           ║
║   SECURE ACCESS TERMINAL         ║
╚══════════════════════════════════╝`}</pre>
                <p className="font-code text-sm text-matrix-green/50 mt-2">
                  Commands: /login  /create-account  /help
                </p>
              </div>
            )}

            {lines.map((line, i) => (
              <p key={i} className="font-code text-sm text-matrix-green whitespace-pre-wrap">
                {line}
              </p>
            ))}

            {promptLabel && (
              <p className="font-code text-sm text-matrix-green">{promptLabel}</p>
            )}

            {fieldError && (
              <p className="font-code text-sm text-alert-red">&gt; {fieldError.toUpperCase()}</p>
            )}
          </div>

          {/* Input bar */}
          <form
            onSubmit={handleSubmit}
            className="shrink-0 border-t border-matrix-green/20 p-3 flex items-center gap-2"
          >
            <span className="font-code text-matrix-green text-sm select-none">$</span>
            <input
              ref={inputRef}
              type={isPasswordField ? 'password' : 'text'}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={placeholder}
              disabled={inputDisabled}
              autoFocus
              autoComplete="off"
              className="flex-1 bg-transparent border-b border-matrix-green/50 text-matrix-green font-code text-sm pb-1 focus:outline-none focus:border-matrix-green caret-matrix-green placeholder:text-matrix-green/20 disabled:opacity-30"
            />
          </form>
        </div>
      </div>
    </div>
  );
}
